import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { useIsCardShell } from '@/components/layout/cardShell/shellVariant';

/**
 * The single loading state for Notifications (`/home/notifications`).
 *
 * Opening it used to show a bare route spinner (the `<Suspense>` fallback), a
 * blank `RoleRoute` frame, then the page's own centred spinner — twice on a
 * reload, since the list was fetched once before the workspace was known and
 * again after. It now draws this: the page's toolbar and a card of
 * notification rows, so the layout appears once and only the rows change.
 */

/** Mirrors `NotificationRow` (non-dense): icon, title, message, time. */
const RowSkeleton: React.FC<{ index: number }> = ({ index }) => (
  <li className="flex items-start gap-3 px-5 py-4">
    <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
    <div className="min-w-0 flex-1 space-y-2 pt-0.5">
      <Skeleton className="h-4 rounded" style={{ width: `${34 + ((index * 13) % 30)}%` }} />
      <Skeleton className="h-3.5 rounded" style={{ width: `${55 + ((index * 17) % 35)}%` }} />
      <Skeleton className="h-3 w-20 rounded" />
    </div>
  </li>
);

export const NotificationsPageSkeleton: React.FC<{ rows?: number }> = ({ rows = 6 }) => {
  const isCardShell = useIsCardShell();
  return (
    <div
      className="mx-auto flex w-full max-w-5xl flex-col"
      aria-busy="true"
      aria-label="Loading notifications"
      data-testid="notifications-page-skeleton"
    >
      {!isCardShell && <Skeleton className="mb-4 h-7 w-40 rounded" />}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Skeleton className="h-9 min-w-0 flex-1 rounded-lg sm:w-[260px] sm:max-w-[280px] sm:flex-none" />
        <div className="ml-auto flex items-center gap-2">
          <Skeleton className="h-9 w-[92px] rounded-lg" />
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-dash-border bg-card shadow-card">
        <ul className="divide-y divide-muted">
          {Array.from({ length: rows }).map((_, i) => (
            <RowSkeleton key={i} index={i} />
          ))}
        </ul>
      </div>
    </div>
  );
};

export default NotificationsPageSkeleton;
